import {
	MutableRefObject,
	SetStateAction,
	useEffect,
	useRef,
	useState,
} from 'react';
import LoadIndicator from '../../../../../../components/blackrock/advisor-center/load-indicator/LoadIndicator';
import { IframeProps, MessageType } from './IframeProps';
import { useLocalCSSRule } from '../../../../../../_helpers/hooks/useLocalCSSRule';
import { useIframeUrlParam } from '../../hooks/useIframeUrlParam';
import { HIDE_TOASTER_EVENT } from '../../../header/_helpers/mainNavToaster.ts';
import { usePrevious } from '../../../../../../_helpers/hooks/usePrevious.ts';
import { createPortal } from 'react-dom';
import { useWindowHeight } from '../../hooks/useWindowHeight.ts';

export const iframeSiderailClass = 'ac-side-rail';
let iframeStatePollTimer: ReturnType<typeof setInterval>;

/**
 * Creates an iframe and sets its height to be the same as its content's height.
 * It can also inject css and custom CSP headers in the iframe.
 */
export const Iframe: React.FC<IframeProps> = ({
	relevantSearchParams,
	canOverrideIframeStyles,
	loadComplete,
	iframeCss,
	iframeTitle = '',
	link,
	visible = true,
	isSideRail,
	sideRailWidth,
	scrollbarWidth,
}) => {
	const [iframeHeight, setIframeHeight] = useState<number>();
	const [resizeObserver, setResizeObserver] = useState<ResizeObserver>();
	const [iframeHidden, setIframeHidden] = useState(true);
	const iframeRef = useRef<HTMLIFrameElement>(null);
	const id = useLocalCSSRule(
		{
			height: iframeHeight + 'px',
		},
		[iframeHeight + 'px'],
		'iframe-'
	);
	const windowHeight = useWindowHeight();

	useEffect(() => {
		const watchIframeState = () => {
			const currentContentDocument = iframeRef.current?.contentDocument;
			const pollTimeout = 50;
			iframeStatePollTimer = setInterval(() => {
				if (
					!iframeRef.current ||
					currentContentDocument === iframeRef.current.contentDocument
				) {
					return;
				}
				clearInterval(iframeStatePollTimer);
				showIframeOnInteractiveState();
				iframeRef.current.contentDocument?.addEventListener(
					'readystatechange',
					() => {
						showIframeOnInteractiveState();
					}
				);
			}, pollTimeout);
		};

		const showIframeOnInteractiveState = () => {
			if (iframeRef.current?.contentDocument?.readyState === 'interactive') {
				iframeLoad();
			}
		};
		watchIframeState();
		return () => {
			clearInterval(iframeStatePollTimer);
		};
	}, []);

	useEffect(() => {
		// eslint-disable-next-line @typescript-eslint/ban-ts-comment
		// @ts-ignore
		toggleClassInIframeForSiderail(iframeRef, isSideRail);
	}, [isSideRail]);

	useEffect(() => {
		visible
			? // eslint-disable-next-line @typescript-eslint/ban-ts-comment
				// @ts-ignore
				resizeObserver?.observe(iframeRef.current.contentDocument.body)
			: // eslint-disable-next-line @typescript-eslint/ban-ts-comment
				// @ts-ignore
				resizeObserver?.unobserve(iframeRef.current.contentDocument.body);
		return () => {
			resizeObserver?.disconnect();
		};
	}, [iframeHeight, visible]);

	const previousIsVisible = usePrevious(visible);

	useEffect(() => {
		// eslint-disable-next-line @typescript-eslint/ban-ts-comment
		// @ts-ignore
		postIFrameVisibility(iframeRef.current, visible);

		if (previousIsVisible && !visible) {
			window.postMessage({ type: HIDE_TOASTER_EVENT });
		}
	}, [visible]);

	useEffect(() => {
		const iFrameMessageHandler = (event: MessageEvent) => {
			if (
				// eslint-disable-next-line @typescript-eslint/ban-ts-comment
				// @ts-ignore
				event.source === iframeRef.current.contentWindow &&
				event.data?.type === MessageType.REQUEST_IFRAME_VISIBILITY
			) {
				// eslint-disable-next-line @typescript-eslint/ban-ts-comment
				// @ts-ignore
				postIFrameVisibility(iframeRef.current, visible);
			}
		};
		window.addEventListener('message', iFrameMessageHandler, false);
		return () => {
			window.removeEventListener('message', iFrameMessageHandler, false);
		};
	}, []);

	const handleIframeLoad = (
		ev: React.BaseSyntheticEvent<Event, HTMLIFrameElement, HTMLIFrameElement>
	) => {
		iframeLoad();
		loadComplete(ev);
	};

	const iframeLoad = () => {
		if (!iframeRef.current?.contentWindow) {
			return;
		}
		const href = iframeRef.current.contentWindow.location.href;
		const host = iframeRef.current.contentWindow.location.host;
		if (!iframeRef.current.contentDocument || !href.includes(host)) {
			return;
		}
		/* Skip these during jsdom based tests */
		// eslint-disable-next-line @typescript-eslint/ban-ts-comment
		// @ts-ignore
		if (iframeRef.current.contentDocument.body) {
			// eslint-disable-next-line @typescript-eslint/ban-ts-comment
			// @ts-ignore
			const height = calculateIframeHeight(iframeRef.current);
			setIframeHeight(height);
			// eslint-disable-next-line @typescript-eslint/ban-ts-comment
			// @ts-ignore
			setResizeObserver(getResizeObserver(setIframeHeight));
			// eslint-disable-next-line @typescript-eslint/ban-ts-comment
			// @ts-ignore
			toggleClassInIframeForSiderail(iframeRef, isSideRail);
		}
		setIframeHidden(false);
	};

	const finalIframeUrl: string = useIframeUrlParam(
		link.url,
		relevantSearchParams
	);

	return (
		<div className="blk-ac-iframe">
			{iframeHidden ? <LoadIndicator /> : ''}
			<iframe
				id={id}
				data-testid={'iframe'}
				onLoad={(
					ev: React.BaseSyntheticEvent<
						Event,
						HTMLIFrameElement,
						HTMLIFrameElement
					>
				) => handleIframeLoad(ev)}
				title={iframeTitle + link.label}
				aria-hidden={iframeHidden || !visible ? 'true' : 'false'}
				ref={iframeRef}
				src={finalIframeUrl}
				key={finalIframeUrl}
			></iframe>
			{iframeRef.current?.contentDocument?.head &&
				createPortal(
					<>
						{windowHeight && (
							<style>{`html, body { min-height: ${windowHeight}px; }`}</style>
						)}
						<style>{`body { padding-left: ${sideRailWidth}px !important; padding-right: ${scrollbarWidth}px !important;}`}</style>
						<style>{`#onetrust-consent-sdk {display: none;}`}</style>
						{canOverrideIframeStyles && <style>{iframeCss}</style>}
					</>,
					iframeRef.current?.contentDocument?.head
				)}
		</div>
	);
};

const toggleClassInIframeForSiderail = (
	iframe: MutableRefObject<HTMLIFrameElement>,
	isSideRail: boolean
) => {
	// eslint-disable-next-line @typescript-eslint/ban-ts-comment
	// @ts-ignore
	const contentBody = iframe.current.contentDocument.body;
	if (isSideRail) {
		contentBody?.classList.add(iframeSiderailClass);
	} else {
		contentBody?.classList.remove(iframeSiderailClass);
	}
};

const postIFrameVisibility = (iframe: HTMLIFrameElement, visible: boolean) => {
	// eslint-disable-next-line @typescript-eslint/ban-ts-comment
	// @ts-ignore
	iframe.contentWindow.postMessage(
		{ type: MessageType.IFRAME_VISIBILITY, payload: visible },
		'*'
	);
};

function calculateIframeHeight(iframe: HTMLIFrameElement) {
	// eslint-disable-next-line @typescript-eslint/ban-ts-comment
	// @ts-ignore
	const iframeBody = iframe.contentDocument.body;
	const height = iframeBody.getBoundingClientRect().height;
	return height;
}

function getResizeObserver(
	setIframeHeight: (value: SetStateAction<number>) => void
) {
	return new ResizeObserver((entries) => {
		for (const entry of entries) {
			const cr = entry.borderBoxSize;
			const height = cr[0].blockSize;
			if (height > 0) {
				setIframeHeight(height);
			}
		}
	});
}
